import { useContext, useEffect, useState } from "react";
import { DashboardContext } from "../../../DashboardContext/DashboardContextProvider";
import {
  isValidEmail,
  isValidName,
  isValidPhoneNumber,
} from "../Helper/formbtnhelper.js";
import NewDate from "../Inputs/NewDate.jsx";
import NewEmail from "../Inputs/NewEmail.jsx";
import NewPhone from "../Inputs/NewPhone.jsx";
import NewText from "../Inputs/NewText.jsx";
import { FormContext } from "../context/FormProvider";
import { scrollToFirstRequiredField } from "../main-helper.js";
import useSetSlide from "./Hooks/setSlide";
import useInvalidInputs from "./Hooks/useInvalidInputs";
import ResponsiveRow from "./ResponsiveRow.jsx";
import "./styles/project-information.css";

// Slide 7: Project Contacts
const ProjectContacts = ({ setSlideStatus, slideState, formStateLoaded }) => {
  const [disabledInputs, setDisabledInputs] = useState([]);
  const {
    slide4,
    slide7,
    setSlide7,
    setIsLoading,
    requestStatus,
    names,
    phoneNumbers,
    emails,
    predictiveTextFetched,
    populateInvalidInputs,
    getInvalidData,
    getInputMessage,
    disabledInputCheck,
  } = useContext(FormContext);

  const { client } = useContext(DashboardContext);
  const { clientId, projectId } = useContext(DashboardContext);

  // On Mount, the page should scroll to the top
  useEffect(() => {
    scrollToFirstRequiredField(slide7);
  }, []);

  // Update Slide State
  const slide7Mutation = useSetSlide(
    clientId,
    projectId,
    7,
    setIsLoading,
    setSlideStatus,
  );

  // Get Invalid Inputs
  const invalidInputsData = useInvalidInputs(
    clientId,
    projectId,
    requestStatus,
  );

  // Populate Invalid Inputs
  useEffect(() => {
    populateInvalidInputs(
      invalidInputsData,
      disabledInputs,
      setDisabledInputs,
      "7",
    );
  }, [invalidInputsData.data, invalidInputsData.isFetched]);

  return (
    <>
      {formStateLoaded ? (
        <>
          <h2 className="question-section">
            {client.name === "Greystone Nevada, LLC"
              ? "Land Manager"
              : `Project Manager`}
          </h2>
          <p className="important-text">
            Please enter the contact information for the project manager or land
            manager
          </p>
          <ResponsiveRow type="general">
            <NewText
              idProp="projectManagerName"
              label="First & Last Name"
              val={slide7.projectManagerName}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? names : []}
              req={!isValidName(slide7.projectManagerName, false)}
              type="name"
              disabledInput={disabledInputCheck(
                "projectManagerName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("projectManagerName", disabledInputs) ===
                false
                  ? getInputMessage(
                      "projectManagerName",
                      invalidInputsData,
                      "7",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "projectManagerName",
                invalidInputsData,
                "7",
              )}
            />
            <NewPhone
              idProp="projectManagerCell"
              label="Cell Phone #"
              val={slide7.projectManagerCell}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={!isValidPhoneNumber(slide7.projectManagerCell, false)}
              disabledInput={disabledInputCheck(
                "projectManagerCell",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("projectManagerCell", disabledInputs) ===
                false
                  ? getInputMessage(
                      "projectManagerCell",
                      invalidInputsData,
                      "7",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "projectManagerCell",
                invalidInputsData,
                "7",
              )}
            />
            <NewPhone
              idProp="projectManagerPhone"
              label="Office Phone #"
              val={slide7.projectManagerPhone}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={!isValidPhoneNumber(slide7.projectManagerPhone, false)}
              disabledInput={disabledInputCheck(
                "projectManagerPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("projectManagerPhone", disabledInputs) ===
                false
                  ? getInputMessage(
                      "projectManagerPhone",
                      invalidInputsData,
                      "7",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "projectManagerPhone",
                invalidInputsData,
                "7",
              )}
            />
            <NewEmail
              idProp="projectManagerEmail"
              label="Email Address"
              val={slide7.projectManagerEmail}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? emails : []}
              req={!isValidEmail(slide7.projectManagerEmail, false)}
              disabledInput={disabledInputCheck(
                "projectManagerEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("projectManagerEmail", disabledInputs) ===
                false
                  ? getInputMessage(
                      "projectManagerEmail",
                      invalidInputsData,
                      "7",
                    )
                  : ""
              }
              invalidData={getInvalidData(
                "projectManagerEmail",
                invalidInputsData,
                "7",
              )}
            />
          </ResponsiveRow>
          <h2 className="question-section">
            {client.name === "Greystone Nevada, LLC"
              ? "Construction Manager"
              : "Site Contact"}
          </h2>
          <p className="important-text">
            {
              "Please enter the contact information of individual to contact for this specific project (The site contact's dust class certification number is required if Argus is to prepare/file the dust control permit for this project)"
            }
          </p>
          <ResponsiveRow type="center">
            <NewText
              idProp="siteContactName"
              label="First & Last Name"
              val={slide7.siteContactName}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? names : []}
              req={false}
              // req={!isValidName(slide7.siteContactName, false)}
              type="name"
              disabledInput={disabledInputCheck(
                "siteContactName",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("siteContactName", disabledInputs) === false
                  ? getInputMessage("siteContactName", invalidInputsData, "7")
                  : ""
              }
              invalidData={getInvalidData(
                "siteContactName",
                invalidInputsData,
                "7",
              )}
            />
            <NewPhone
              idProp="siteContactPhone"
              label="Phone #"
              val={slide7.siteContactPhone}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? phoneNumbers : []}
              req={false}
              // req={!isValidPhoneNumber(slide7.siteContactPhone, false)}
              disabledInput={disabledInputCheck(
                "siteContactPhone",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("siteContactPhone", disabledInputs) === false
                  ? getInputMessage("siteContactPhone", invalidInputsData, "7")
                  : ""
              }
              invalidData={getInvalidData(
                "siteContactPhone",
                invalidInputsData,
                "7",
              )}
            />
            <NewEmail
              idProp="siteContactEmail"
              label="Email Address"
              val={slide7.siteContactEmail}
              state={slide7}
              setState={setSlide7}
              debouncedSave={slide7Mutation}
              setIsLoading={setIsLoading}
              autoData={predictiveTextFetched ? emails : []}
              req={false}
              // req={!isValidEmail(slide7.siteContactEmail, false)}
              disabledInput={disabledInputCheck(
                "siteContactEmail",
                disabledInputs,
              )}
              disabledInputMessage={
                disabledInputCheck("siteContactEmail", disabledInputs) === false
                  ? getInputMessage("siteContactEmail", invalidInputsData, "7")
                  : ""
              }
              invalidData={getInvalidData(
                "siteContactEmail",
                invalidInputsData,
                "7",
              )}
            />
          </ResponsiveRow>
          {slide4.filePermitRequest && (
            <ResponsiveRow type="center">
              <NewText
                idProp="dustCertificationNumber"
                label="Dust Certification Number"
                type="dustCert"
                val={slide7.dustCertificationNumber}
                state={slide7}
                setState={setSlide7}
                debouncedSave={slide7Mutation}
                setIsLoading={setIsLoading}
                autoData={predictiveTextFetched ? names : []}
                req={
                  slide4.filePermitRequest && !slide7.dustCertificationNumber
                    ? true
                    : false
                }
                disabledInput={disabledInputCheck(
                  "dustCertificationNumber",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck(
                    "dustCertificationNumber",
                    disabledInputs,
                  ) === false
                    ? getInputMessage(
                        "dustCertificationNumber",
                        invalidInputsData,
                        "7",
                      )
                    : ""
                }
                invalidData={getInvalidData(
                  "dustCertificationNumber",
                  invalidInputsData,
                  "7",
                )}
              />
              <NewDate
                idProp="dustCertificationExpiration"
                label="Dust Certification Expiration"
                state={slide7}
                setState={setSlide7}
                val={slide7.dustCertificationExpiration}
                debouncedSave={slide7Mutation}
                setIsLoading={setIsLoading}
                req={
                  slide4.filePermitRequest &&
                  slide7.dustCertificationExpiration === ""
                    ? true
                    : false
                }
                disabledInput={disabledInputCheck(
                  "dustCertificationExpiration",
                  disabledInputs,
                )}
                disabledInputMessage={
                  disabledInputCheck(
                    "dustCertificationExpiration",
                    disabledInputs,
                  ) === false
                    ? getInputMessage(
                        "dustCertificationExpiration",
                        invalidInputsData,
                        "7",
                      )
                    : ""
                }
                invalidData={getInvalidData(
                  "dustCertificationExpiration",
                  invalidInputsData,
                  "7",
                )}
              />
            </ResponsiveRow>
          )}
        </>
      ) : (
        <p className="slide-loading">Loading...</p>
      )}
    </>
  );
};

export default ProjectContacts;
